import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { OrganizationAvatar } from "@basecomponents/OrganizationAvatar";
import { getShiComBaseUrl } from "@lib/shiComBaseUrl";
import { pushEvent, telemetryProps } from "shared-ui/src/instrumentation/gtm";
import {
  parseOptionId,
  type SwitcherData,
  type SwitcherTenantGroup,
  toSwitcherData,
} from "./storefrontSwitcherData";
import { useGetUserContext, useSwitchUserContext } from "./useUserContext";
import { readPendingSourceCurrency, saveCartSwitchSummary } from "./cartSwitchSummary";
import useStorefrontCurrency from "@features/ecommerce/hooks/useStorefrontCurrency";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import useGetShoppingCart from "@features/unified-user-header-feature/hooks/useGetShoppingCart";
import { CART_WILL_NOT_RECALCULATE_MESSAGE } from "./StorefrontSwitchResultNotice";

export type StorefrontSwitcherSource =
  | "tenant_nametag"
  | "storefront_nametag"
  | "user_menu"
  | "org_unit_menu";

export type StorefrontSwitcherDialogProps = Readonly<{
  open: boolean;
  onClose: () => void;
  source: StorefrontSwitcherSource;
  /** Opens with the current tenant expanded so the user lands on its storefronts. */
  expandCurrentTenant?: boolean;
}>;

function TenantSection({
  group,
  selectedId,
  currentOptionId,
  showCurrentBadge,
  collapsible,
  expandCurrentTenant,
  source,
}: Readonly<{
  group: SwitcherTenantGroup;
  selectedId: string | null;
  currentOptionId: string | null;
  showCurrentBadge: boolean;
  collapsible: boolean;
  expandCurrentTenant: boolean;
  source: StorefrontSwitcherSource;
}>) {
  const headingId = `storefront-switcher-${group.optionId}`;
  const isTenantSelected = selectedId === group.optionId;
  const [expanded, setExpanded] = useState(
    !collapsible || (expandCurrentTenant && group.isCurrent),
  );
  const storefrontCount = group.storefronts.length;

  return (
    <Box
      role="group"
      aria-labelledby={headingId}
      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
    >
      <Paper
        variant="outlined"
        component="label"
        sx={{
          p: 1.5,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          bgcolor: "background.paper",
          borderColor: isTenantSelected ? "primary.main" : "divider",
          borderWidth: isTenantSelected ? 2 : 1,
        }}
      >
        <Radio
          value={group.optionId}
          sx={{ p: 0 }}
          inputProps={{ "aria-label": `${group.tenantName} (entire tenant)` }}
        />
        <OrganizationAvatar
          size={36}
          ariaLabelNoLogo={`${group.tenantName} icon`}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0.25,
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
          >
            <Typography
              id={headingId}
              variant="body1"
              noWrap
              sx={{
                color: isTenantSelected ? "primary.main" : "text.primary",
                fontWeight: isTenantSelected ? 500 : 600,
              }}
            >
              {group.tenantName}
            </Typography>
            <Chip
              label="Tenant"
              size="small"
              sx={{
                bgcolor: "action.selected",
                color: "text.secondary",
                flexShrink: 0,
              }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" noWrap>
            {storefrontCount === 0
              ? "No Storefront"
              : storefrontCount === 1
                ? "View Storefront"
                : `View and select from ${storefrontCount}`}
          </Typography>
        </Box>
        {showCurrentBadge && group.isCurrent && (
          <Chip
            label="Current"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ flexShrink: 0 }}
          />
        )}
        {collapsible && storefrontCount > 0 && (
          <Tooltip title="View Storefronts">
            <IconButton
              size="small"
              aria-label={`${expanded ? "Collapse" : "Expand"} ${group.tenantName} storefronts`}
              aria-expanded={expanded}
              {...telemetryProps({
                name: "storefront_switch_expand_tenant",
                section: source,
                label: group.tenantName,
              })}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setExpanded((previous) => !previous);
              }}
            >
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  color: "text.secondary",
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              />
            </IconButton>
          </Tooltip>
        )}
      </Paper>

      {storefrontCount > 0 && (
        <Collapse
          in={!collapsible || expanded}
          timeout="auto"
          unmountOnExit={collapsible}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              ml: { xs: 2, sm: 3 },
              pl: { xs: 1.5, sm: 2 },
              borderLeft: 2,
              borderLeftStyle: "solid",
              borderColor: "divider",
            }}
          >
            {group.storefronts.map((storefront) => {
              const isSelected = selectedId === storefront.optionId;
              return (
                <Paper
                  key={storefront.optionId}
                  variant="outlined"
                  component="label"
                  sx={{
                    p: 1.5,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    bgcolor: "background.paper",
                    borderColor: isSelected ? "primary.main" : "divider",
                    borderWidth: isSelected ? 2 : 1,
                  }}
                >
                  <Radio
                    value={storefront.optionId}
                    sx={{ p: 0 }}
                    inputProps={{ "aria-label": storefront.name }}
                  />
                  <OrganizationAvatar
                    size={32}
                    logoUrl={storefront.logoUrl}
                    ariaLabelLogo={`${storefront.name} logo`}
                    ariaLabelNoLogo={`${storefront.name} icon`}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        color: isSelected ? "primary.main" : "text.primary",
                        fontWeight: isSelected ? 500 : 400,
                      }}
                    >
                      {storefront.name}
                    </Typography>
                    <Chip
                      label="Storefront"
                      size="small"
                      sx={{
                        bgcolor: "action.selected",
                        color: "text.secondary",
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                  {currentOptionId === storefront.optionId && (
                    <Chip
                      label="Current"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ flexShrink: 0 }}
                    />
                  )}
                </Paper>
              );
            })}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

export function StorefrontSwitcherDialog({
  open,
  onClose,
  source,
  expandCurrentTenant = false,
}: StorefrontSwitcherDialogProps) {
  const { user } = useAuth0();
  const { getClaimValueAsBoolean } = useClaims();
  const isImpersonating = getClaimValueAsBoolean("isImpersonating");
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: response, isLoading, isError } = useGetUserContext(open);
  const switchContext = useSwitchUserContext();
  const { currency, isLoading: isCurrencyLoading } = useStorefrontCurrency();
  const { data: cart } = useGetShoppingCart();
  const cartLineCount = cart?.length ?? 0;
  const shiComBaseUrl = useMemo(() => getShiComBaseUrl(), []);
  const data: SwitcherData = useMemo(
    () => toSwitcherData(response, shiComBaseUrl),
    [response, shiComBaseUrl],
  );
  // An unfinished switch blocks every other choice server-side, so start on its target.
  const initialOptionId = data.pendingOptionId ?? data.currentOptionId;
  const [selectedId, setSelectedId] = useState<string | null>(initialOptionId);
  const [error, setError] = useState<string | undefined>();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const switching = switchContext.isPending;
  // Dismissing mid-switch would unmount the dialog and drop the success reload, leaving stale claims.
  const handleClose = () => {
    if (!switching) {
      onClose();
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedId(initialOptionId);
    setError(undefined);
    const timer = setTimeout(() => {
      const radios = contentRef.current?.querySelectorAll<HTMLInputElement>(
        'input[type="radio"]',
      );
      const checked = Array.from(radios ?? []).find((radio) => radio.checked);
      (checked ?? radios?.[0])?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [open, initialOptionId]);

  const selection = selectedId ? parseOptionId(selectedId) : null;
  const isRetry = !!selection && selectedId === data.pendingOptionId;
  const isChange = !!selection && selectedId !== data.currentOptionId;
  // The server rejects every other choice until the unfinished switch completes.
  const canConfirm =
    (isRetry || (isChange && !data.pendingOptionId)) &&
    !switching &&
    !isCurrencyLoading;
  const switchKind = selection?.kind ?? "tenant";
  const confirmLabel = isRetry
    ? "Retry Switch"
    : switchKind === "tenant"
      ? "Switch Tenant"
      : "Switch Storefront";
  const multipleTenants = data.tenantGroups.length > 1;
  const hasCurrentStorefront =
    data.currentOptionId?.startsWith("storefront:") ?? false;
  const cartNotRecalculated =
    isImpersonating && isChange && selection?.kind === "storefront";
  const cartWarning =
    !(isChange || isRetry) || cartLineCount === 0
      ? null
      : cartNotRecalculated
        ? CART_WILL_NOT_RECALCULATE_MESSAGE
        : selection?.kind === "storefront"
          ? `Your cart has ${cartLineCount} ${cartLineCount === 1 ? "item" : "items"}. They will be checked against this storefront's catalog: prices may change, and items it doesn't offer will be removed.`
          : hasCurrentStorefront
            ? "Your cart will be kept, but you won't be able to shop or check out until you choose a storefront."
            : null;

  const handleSwitch = () => {
    if (!selection) {
      return;
    }
    setError(undefined);
    // After a failed cart migration the cart still holds the original storefront's prices, but the
    // current currency is already the target's.
    const previousCurrency = (isRetry
      ? readPendingSourceCurrency(user?.sub)
      : undefined) ?? {
      code: currency.code,
      symbol: currency.symbol,
      locale: currency.locale,
    };
    switchContext.mutate(selection, {
      onSuccess: (result) => {
        saveCartSwitchSummary(
          result,
          user?.sub,
          cartNotRecalculated && cartLineCount > 0,
          previousCurrency,
        );
        // Deliberately always a full reload to home: claims, permissions, navigation and every cached
        // query change with the context, and the user may no longer be allowed on the current page.
        window.location.href = window.location.origin;
      },
      onError: () => {
        pushEvent({
          event: "storefront_switch_failed",
          name: "storefront_switch_failed",
          section: source,
          action: "error",
          label: switchKind,
        });
        setError(
          switchKind === "tenant"
            ? "We could not switch tenants. Please try again."
            : "We could not switch storefronts. Please try again.",
        );
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      aria-labelledby="storefront-switcher-title"
      aria-describedby="storefront-switcher-description"
      slotProps={{
        paper: {
          sx: (theme) => ({
            width: {
              xs: "100%",
              sm: `calc(100vw - ${theme.spacing(8)})`,
              md: theme.spacing(87.5),
            },
            maxWidth: { xs: "100%", sm: theme.spacing(112.5) },
          }),
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 1.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          bgcolor: "background.paper",
        }}
      >
        <Box>
          <Typography id="storefront-switcher-title" variant="h6">
            {expandCurrentTenant ? "Switch Storefront" : "Switch Tenant"}
          </Typography>
          <Typography
            id="storefront-switcher-description"
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            Choose a storefront, or switch to a tenant for general access.
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={switching}
          aria-label="Close switch storefront dialog"
          {...telemetryProps({
            name: "storefront_switch_close",
            section: source,
            label: "Close",
          })}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      <DialogContent
        ref={contentRef}
        sx={{ px: 3, py: 3, bgcolor: "background.default" }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {data.pendingOptionId && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your last storefront switch didn&apos;t finish checking your cart.
            Retry it before switching anywhere else.
          </Alert>
        )}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress aria-label="Loading storefronts" />
          </Box>
        ) : isError ? (
          <Alert severity="error">
            We could not load your tenants and storefronts. Please try again.
          </Alert>
        ) : data.tenantGroups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No other storefronts are available to switch to right now.
          </Typography>
        ) : (
          <RadioGroup
            aria-label="Select a tenant or storefront"
            name="storefront-switcher"
            value={selectedId ?? ""}
            onChange={(_, value) => setSelectedId(value)}
          >
            {data.tenantGroups.map((group, index) => (
              <Box key={group.optionId}>
                <TenantSection
                  group={group}
                  selectedId={selectedId}
                  currentOptionId={data.currentOptionId}
                  showCurrentBadge={multipleTenants}
                  collapsible={multipleTenants}
                  expandCurrentTenant={expandCurrentTenant}
                  source={source}
                />
                {index < data.tenantGroups.length - 1 && (
                  <Divider sx={{ my: 2 }} />
                )}
              </Box>
            ))}
          </RadioGroup>
        )}
        {cartWarning && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {cartWarning}
          </Alert>
        )}
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          justifyContent: "space-between",
          bgcolor: "background.paper",
        }}
      >
        <Button
          color="inherit"
          onClick={handleClose}
          disabled={switching}
          {...telemetryProps({
            name: "storefront_switch_cancel",
            section: source,
            label: switchKind,
          })}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSwitch}
          disabled={!canConfirm}
          startIcon={
            switching ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
          {...telemetryProps({
            name: "storefront_switch_confirm",
            section: source,
            label: switchKind,
          })}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
